import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface OverlayFocusOptions {
  open: boolean;
  panelRef: RefObject<HTMLElement | null>;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  trap?: boolean;
  preserveExternalFocus?: boolean;
}

/**
 * Gives custom menus and dialogs one predictable keyboard contract:
 * focus enters on open, Escape closes, Tab stays inside modal-style panels,
 * and focus returns to the control that opened the overlay.
 */
export function useOverlayFocus({
  open,
  panelRef,
  triggerRef,
  onClose,
  trap = true,
  preserveExternalFocus = false,
}: OverlayFocusOptions) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const panel = panelRef.current;
    const restoreTarget = triggerRef.current ??
      (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const focusPanel = window.requestAnimationFrame(() => {
      const active = document.activeElement;
      if (preserveExternalFocus && active instanceof Element && active !== restoreTarget && !panel?.contains(active)
        && active.closest('.shell-header, [role="dialog"]')) return;
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus({ preventScroll: true });
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (!trap || event.key !== 'Tab') return;

      const focusable = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [],
      ).filter((element) => element.getClientRects().length > 0);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusPanel);
      document.removeEventListener('keydown', onKeyDown);
      const active = document.activeElement;
      if (restoreTarget?.isConnected && (!preserveExternalFocus || active === document.body || active === restoreTarget || (active && panel?.contains(active)))) {
        restoreTarget.focus();
      }
    };
  }, [open, panelRef, trap, triggerRef, preserveExternalFocus]);
}
